import { BoundaryOfInterest } from '../../../../models/map-box.model'
import { SharedUserService } from '../../../../services/shared-user.service'
import { Colors, Crop, CroptypeColor, FeatureCollection, Location } from '@agroone/entities'
import {
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  viewChild,
} from '@angular/core'
import { BndButton } from '@bonduelle-it/storybook'
import { TranslatePipe } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'
import { AoConfirmDialogComponent } from '../../../ao-confirm-dialog/ao-confirm-dialog.component'
import { MapStyleType } from '../../enums/map-style-type.enum'
import { MapOptions } from '../../interfaces/map-options.interface'
import { toMapCenter } from '../../utils/map-center.util'
import { CropService } from '../../services/crop.service'
import { ImportService } from '../../services/import.service'
import { MapCoreService } from '../../services/map-core.service'
@Component({
  selector: 'app-map-draw',
  templateUrl: './map-draw.component.html',
  styleUrls: ['./map-draw.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BndButton, TranslatePipe, AoConfirmDialogComponent],
})
export class MapDrawComponent implements OnInit, OnDestroy {
  readonly data = input.required<MapOptions>()
  readonly cropTypeColors = input.required<CroptypeColor[]>()
  readonly regionName = input.required<string>()
  readonly defaultAreaUnit = input<string>()
  readonly isReadOnly = input(false)

  readonly cancel = output<void>()
  readonly confirm = output<{
    boundary: FeatureCollection
    event: 'close'
  }>()

  readonly mapContainer = viewChild.required<ElementRef>('mapcontainer')

  private readonly _cropColor = Colors.CROPS
  private readonly _cropOtherFarmsColor = Colors.OTHER_CROPS
  private readonly _cropService = inject(CropService)
  protected readonly _mapCoreService = inject(MapCoreService)
  private readonly _userService = inject(SharedUserService)
  private readonly _importService = inject(ImportService)
  private readonly _document = inject(DOCUMENT)
  private readonly MAPBOX_STYLES = 'https://api.mapbox.com/mapbox-gl-js/v3.15.0/mapbox-gl.css'
  private readonly MAPBOX_DRAW_STYLES =
    'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.4.3/mapbox-gl-draw.css'

  async ngOnInit() {
    this._injectStylesheet(this.MAPBOX_STYLES)
    this._injectStylesheet(this.MAPBOX_DRAW_STYLES)
    await this._initMap(this.data())
    await this._initCropBoundariesAround(this.data())
    const data = this.data()
    this._initOtherCropBoundaries(data)
    await this._mapCoreService.createEditableBoundaries(data.boundary, this.defaultAreaUnit(), !this.isReadOnly())
  }

  ngOnDestroy() {
    this._mapCoreService.destroy()
  }

  private _injectStylesheet(href: string): void {
    if (this._document.querySelector(`link[href="${href}"]`)) {
      return
    }

    const link = this._document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.type = 'text/css'

    this._document.head.appendChild(link)
  }

  private async _initCropBoundariesAround(options: MapOptions) {
    // Create geohashes based on the map center & boundary
    if (!options.location) return

    const { latitude, longitude } = options.location
    const geohashes = await this._mapCoreService.createGeohashes(
      {
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
      options.boundary,
      options.address,
    )
    if (!geohashes) return

    // Get crops within these geohashes
    const crops = await this._cropService.getByGeohash(geohashes, this.regionName())
    if (!crops?.length) return

    // Fetch boundaries for these crops
    // Current crop is not "near by", we filter it
    const cropsAround = (await firstValueFrom(this._cropService.getCropBoundaries(crops)))?.filter(
      (crop) => crop.id === undefined || !this.data()?.entitiesIds?.includes(crop.id),
    )

    this._drawCropBoundaries('boundariesNearby', cropsAround, this._cropColor)
  }

  private _initOtherCropBoundaries(data: MapOptions) {
    if (data?.crops) {
      const cropsOtherFarmsFeatures: Crop[] = data.crops?.filter((crop) => crop.farmId !== data.farmId)
      this._drawCropBoundaries('boundariesAround', cropsOtherFarmsFeatures, this._cropOtherFarmsColor)
    }
  }

  private async _drawCropBoundaries(id: string, crops: Crop[], color: string) {
    if (crops) {
      const grouped = this._groupByCropColor(crops, color)
      this._mapCoreService.createBoundaries(id, grouped)
    }
  }

  private _groupByCropColor(crops: Crop[], color: string) {
    return crops.reduce(
      (acc, crop) => {
        acc[color] ??= { boundaries: [] }
        acc[color].boundaries.push(new BoundaryOfInterest(crop.id, crop.boundary))
        return acc
      },
      {} as Record<string, { boundaries: BoundaryOfInterest[] }>,
    )
  }

  public async onButtonOk(): Promise<void> {
    const boundary = this._mapCoreService.getBoundary()
    this.confirm.emit({ boundary: boundary as FeatureCollection, event: 'close' })
  }

  public async import(event: Event): Promise<void> {
    const fileList = (event?.target as HTMLInputElement)?.files
    if (fileList) {
      // Read the files (zip, shp or kml)
      const files = await this._importService.filesToKmlOrShapefile(Array.from(fileList))
      // Extract the feature collection
      const collection = await this._importService.extract(files.shp[0] ?? files.kml[0])
      this._mapCoreService.resetMap()

      // Create the updated data with the new boundary
      const updatedData = {
        ...this.data(),
        boundary: collection as FeatureCollection,
        location: undefined, // Reset location so it's recalculated from the new boundary
      }

      // Re-init the map with the new boundary
      await this._initMap(updatedData)
      await this._initCropBoundariesAround(updatedData)
      this._initOtherCropBoundaries(updatedData)
      await this._mapCoreService.createEditableBoundaries(collection as FeatureCollection, this.defaultAreaUnit())
    }
  }

  private async _initMap(data: MapOptions): Promise<void> {
    const location = this._resolveLocation(data)
    const options = this._mapOptions(data)

    if (location || data.boundary) {
      await this._mapCoreService.init({
        container: this.mapContainer(),
        boundary: data.boundary,
        center: toMapCenter(location),
        ...options,
      })
      return
    }
    // No location, we use adress or region name
    const address = data.address || this._userService.requireCurrentUser().regionName
    await this._mapCoreService.initByAddress(this.mapContainer(), address, options)
  }

  private _mapOptions(data: MapOptions) {
    return {
      drawable: data.displayMode === 'draw' && !this.isReadOnly(),
      stencil: data.stencil,
    }
  }

  private _resolveLocation(data: MapOptions): Location | null {
    const loc = data.location
    const hasValidLocation = loc && (loc.latitude || loc.longitude)

    if (hasValidLocation) return loc

    if (!data.boundary?.features?.length) return null

    try {
      return this._mapCoreService.getCenterLocationFromFeatures(
        data.boundary.features as GeoJSON.Feature<GeoJSON.Polygon>[],
      )
    } catch (err) {
      console.error('Error calculating boundary center:', err)
      return null
    }
  }

  toggleMapStyle() {
    this._mapCoreService.toggleMapStyle()
  }

  getCurrentStyleType(): MapStyleType {
    return this._mapCoreService.getCurrentStyleType()
  }
}
